<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

const showMenu = ref(false);
const topOfPage = ref(true);
const activeSection = ref("");
const isScrolling = ref(false);
const emit = defineEmits(['topOfPage']);
let scrollResetTimer: number | null = null;

const toggleNav = () => (showMenu.value = !showMenu.value);

const updateTopOfPage = () => {
    const isTop = window.scrollY <= 10;
    topOfPage.value = isTop;
    emit('topOfPage', isTop);
};

const updateActiveLink = () => {
    const sections = ["prizes", "rules", "submission", "ranking", "regulations", "contact"]
        .map((id) => {
            const section = document.getElementById(id);
            return section ? { id, top: section.offsetTop } : null;
        })
        .filter((section): section is { id: string; top: number } => section !== null);

    if (!sections.length) {
        activeSection.value = "";
        return;
    }

    const scrollPosition = window.scrollY + window.innerHeight / 2;
    let currentActive = "";

    for (let index = 0; index < sections.length; index += 1) {
        const currentSection = sections[index];
        const nextSection = sections[index + 1];
        const sectionEnd = nextSection ? (currentSection.top + nextSection.top) / 2 : Number.POSITIVE_INFINITY;

        if (scrollPosition >= currentSection.top && scrollPosition < sectionEnd) {
            currentActive = currentSection.id;
            break;
        }
    }

    if (!currentActive && scrollPosition >= sections[sections.length - 1].top) {
        currentActive = sections[sections.length - 1].id;
    }

    if (!currentActive && scrollPosition < sections[0].top) {
        activeSection.value = "";
        return;
    }

    activeSection.value = currentActive;
};

const handleScroll = () => {
    isScrolling.value = true;
    if (scrollResetTimer !== null) {
        window.clearTimeout(scrollResetTimer);
    }
    scrollResetTimer = window.setTimeout(() => {
        isScrolling.value = false;
    }, 120);

    updateTopOfPage();
    updateActiveLink();
};

onMounted(() => {
    updateTopOfPage();
    updateActiveLink();
    window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
    if (scrollResetTimer !== null) {
        window.clearTimeout(scrollResetTimer);
    }
    window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
    <header class="menu-shell z-50 relative">
        <nav :class="[topOfPage ? 'bg-default-white' : 'onScroll']"
            class="desktop-menu px-6 py-4 hidden lg:flex w-full text-primary justify-center items-center gap-8"
            :data-scrolling="isScrolling ? 'true' : 'false'">
            <a href="#prizes" :class="['menu-link', { 'is-active': activeSection === 'prizes' }]" style="animation-delay: 0.25s;">NAGRODY</a>
            <a href="#rules" :class="['menu-link', { 'is-active': activeSection === 'rules' }]" style="animation-delay: 0.32s;">ZASADY</a>
            <a href="#submission" :class="['menu-link', { 'is-active': activeSection === 'submission' }]" style="animation-delay: 0.39s;">WEŹ UDZIAŁ</a>
            <a href="#ranking" :class="['menu-link', { 'is-active': activeSection === 'ranking' }]" style="animation-delay: 0.46s;">RANKING</a>
            <a href="#regulations" :class="['menu-link', { 'is-active': activeSection === 'regulations' }]" style="animation-delay: 0.53s;">REGULAMIN</a>
            <a href="#contact" :class="['menu-link', { 'is-active': activeSection === 'contact' }]" style="animation-delay: 0.60s;">KONTAKT</a>
        </nav>
        <nav class="mobile-bar px-5 py-3 w-full flex items-center justify-between block lg:hidden">
            <button type="button" @click="toggleNav" class="mobile-toggle" aria-label="Otwórz menu" :aria-expanded="showMenu">
                <div id="nav-icon">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </button>
            <img src="@/assets/images/hero/tylko_zabka.webp" alt="tylko w Żabka" class="mobile-badge">
        </nav>

        <transition name="overlay">
            <div v-if="showMenu" class="mobile-overlay lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
                <button type="button" @click="toggleNav" class="overlay-close" aria-label="Zamknij menu">
                    <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
                    </svg>
                </button>
                <ul class="overlay-list">
                    <li><a href="#prizes" :class="{ 'is-active': activeSection === 'prizes' }" @click="toggleNav">NAGRODY</a></li>
                    <li><a href="#rules" :class="{ 'is-active': activeSection === 'rules' }" @click="toggleNav">ZASADY</a></li>
                    <li><a href="#submission" :class="{ 'is-active': activeSection === 'submission' }" @click="toggleNav">WEŹ UDZIAŁ</a></li>
                    <li><a href="#ranking" :class="{ 'is-active': activeSection === 'ranking' }" @click="toggleNav">RANKING</a></li>
                    <li><a href="#regulations" :class="{ 'is-active': activeSection === 'regulations' }" @click="toggleNav">REGULAMIN</a></li>
                    <li><a href="#contact" :class="{ 'is-active': activeSection === 'contact' }" @click="toggleNav">KONTAKT</a></li>
                </ul>
            </div>
        </transition>
    </header>
</template>

<style scoped>
.desktop-menu {
    animation: menu-slide-down 0.55s ease 0.1s both;
}

.menu-link {
    position: relative;
    opacity: 0;
    animation: menu-fade-in 0.3s ease forwards;
}

.menu-link::after,
ul a::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -0.35rem;
    width: 100%;
    height: 2px;
    background: var(--color-default-red);
    transform: translateX(-50%) scaleX(0);
    transform-origin: center;
    transition: transform 0.3s ease;
}

.desktop-menu[data-scrolling="false"] .menu-link:hover::after,
ul a:hover::after {
    transform: translateX(-50%) scaleX(1);
}

.menu-link.is-active::after,
ul a.is-active::after {
    transform: translateX(-50%) scaleX(1);
}

ul.open {
    max-height:650px;
    -webkit-transition: all 0.5s ease-in-out;
    -moz-transition: all 0.5s ease-in-out;
    -ms-transition: all 0.5s ease-in-out;
    -o-transition: all 0.5s ease-in-out;
    transition: all 0.5s ease-in-out;
}

ul {
    overflow: hidden;
    max-height: 0;

}

nav.open {
    transition: background-color 0s ease-in;
}

nav {
    transition: background-color 0.2s ease-out 0.4s;
}

ul a {
    position: relative;
    display: inline-block;
}

#nav-icon {
    width: 40px;
    height: 35px;
    position: relative;
    margin: 10px auto;
    -webkit-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
    -webkit-transition: .5s ease-in-out;
    -moz-transition: .5s ease-in-out;
    -o-transition: .5s ease-in-out;
    transition: .5s ease-in-out;
    cursor: pointer;
}

#nav-icon span {
    display: block;
    position: absolute;
    height: 5px;
    width: 100%;
    background: var(--color-primary);
    opacity: 1;
    left: 0;
    -webkit-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
    -webkit-transition: .25s ease-in-out;
    -moz-transition: .25s ease-in-out;
    -o-transition: .25s ease-in-out;
    transition: .25s ease-in-out;
}

#nav-icon span:nth-child(1) {
    top: 6px;
}

#nav-icon span:nth-child(2) {
    top: 15px;
}

#nav-icon span:nth-child(3) {
    top: 24px;
}

#nav-icon.open span:nth-child(1) {
    top: 9px;
    width: 0%;
    left: 50%;
}

#nav-icon.open span:nth-child(2) {
    -webkit-transform: rotate(45deg);
    -moz-transform: rotate(45deg);
    -o-transform: rotate(45deg);
    transform: rotate(45deg);
}

#nav-icon.open span:nth-child(3) {
    -webkit-transform: rotate(-45deg);
    -moz-transform: rotate(-45deg);
    -o-transform: rotate(-45deg);
    transform: rotate(-45deg);
}

#nav-icon.open span:nth-child(2), #nav-icon.open span:nth-child(3) {
    top: 14px;
}

.onScroll {
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.10);
    background: var(--color-default-white);
    position: fixed;
    z-index: 10;
    transition: box-shadow 0.3s ease, background-color 0.3s ease;
}

/* ---- Mobile header bar ---- */
.mobile-bar {
    position: relative;
    z-index: 30;
    background: transparent;
}

.mobile-toggle {
    background: none;
    padding: 0;
    margin: 0;
}

#nav-icon {
    width: 32px;
    height: 24px;
}

#nav-icon span {
    height: 3px;
    border-radius: 2px;
}

#nav-icon span:nth-child(1) { top: 2px; }
#nav-icon span:nth-child(2) { top: 10px; }
#nav-icon span:nth-child(3) { top: 18px; }

.mobile-badge {
    height: 28px;
    width: auto;
}

/* ---- Mobile full-screen overlay ---- */
.mobile-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: var(--color-primary);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

@media only screen and (min-width: 1024px) {
    .menu-shell {
        min-height: 101px;
    }

    .mobile-overlay {
        display: none;
    }
}

.overlay-close {
    position: absolute;
    top: 1.25rem;
    right: 1.5rem;
    background: none;
    padding: 0.25rem;
    margin: 0;
    color: var(--color-default-red);
    line-height: 0;
    cursor: pointer;
}

.overlay-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    max-height: none;
    overflow: visible;
}

.overlay-list a {
    color: var(--color-default-white);
    font-size: 1.5rem;
    letter-spacing: 0.05em;
    font-weight: 500;
}

.overlay-enter-active,
.overlay-leave-active {
    transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
    opacity: 0;
}

@keyframes menu-slide-down {
    from {
        transform: translateY(-100%);
    }

    to {
        transform: translateY(0);
    }
}

@keyframes menu-fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}
</style>
